import { z } from "zod";

import { PATIENT_CHECK_ANSWERS } from "./patientCheckAnswers";

const today = new Date().toISOString().split("T")[0];

/*
 * The patient checks are three-state, not boolean (KIN-2056): a tick box could not say whether an
 * empty box meant no or meant nobody asked. The form holds the answer as one of these three codes
 * and the mutation turns it into the nullable boolean the column stores, so "not asked" survives
 * validation instead of being flattened to false here.
 */
const patientCheckAnswer = z.enum([
  PATIENT_CHECK_ANSWERS.yes,
  PATIENT_CHECK_ANSWERS.no,
  PATIENT_CHECK_ANSWERS.notAsked,
]);

/*
 * What both pregnancy forms ask. The hospital and the number of foetuses are deliberately absent:
 * they are the two fields the forms disagree about (KIN-2060), and each schema states its own rule
 * for them below rather than sharing an "optional" that would quietly stop validating the edit form.
 */
const sharedPregnancyFields = {
  gravida: z.string().min(1, { message: "Enter the number of pregnancies." }),
  para: z.string().min(1, { message: "Enter the number of births." }),
  hasClinicHCGPositive: patientCheckAnswer,
  smoker: patientCheckAnswer,
  noFamilyHistoryOfCongenitalAbnormality: patientCheckAnswer,
  hasHadChickenPox: patientCheckAnswer,
  lastMenstrualPeriod: z
    .string({ message: "Please enter the date of the last menstrual period." })
    .min(1, { message: "Please enter the date of the last menstrual period." })
    .refine((val) => !val || val <= today, {
      message: "Last menstrual period cannot be in the future.",
    }),
  eddMethod: z.string().min(1, { message: "Please select the estimated due date method." }),
  estimatedDueDate: z.string().optional(),
  conductedByStaffMemberId: z.string().min(1, { message: "Please select a doctor." }),
};

const isParaWithinGravida = (data) => {
  if (!data.gravida || !data.para) return true;

  return parseInt(data.para, 10) <= parseInt(data.gravida, 10);
};

const PARA_WITHIN_GRAVIDA_ERROR = {
  message: "Para cannot be greater than Gravida.",
  path: ["para"],
};

/*
 * Creating a pregnancy (KIN-2060). Neither the hospital nor the number of foetuses is asked for:
 * both arrive on the maternity hospital's own messages — the backend fills the hospital from MSH.4
 * of the first one, and takes the foetus count from the hospital's messages as well — and neither
 * is answerable at a first visit.
 *
 * They are omitted rather than made optional so that nothing can read them off the parsed values by
 * accident: zod strips keys it does not describe, and the form posts the two settled values
 * (no hospital, one foetus) explicitly instead.
 */
export const addPregnancyFormSchema = z
  .object(sharedPregnancyFields)
  .refine(isParaWithinGravida, PARA_WITHIN_GRAVIDA_ERROR);

/*
 * Editing a pregnancy. Both fields are asked for here, because this is the only place a wrong
 * hospital, a transfer of care or a scan that found twins can be corrected — the backend fills the
 * hospital only while it is blank.
 *
 * The hospital is optional: a pregnancy created before its first hospital message has none, and
 * requiring one would stop its gravida being fixed. The send is gated on it separately
 * (getMissingMncmsFields), so an empty hospital blocks a message rather than a correction.
 *
 * The foetus count is required only when the record already holds one. Where it does, clearing the
 * box would be posted as a single foetus and would silently demote a twin pregnancy, so blank is
 * not an answer. Where it does not — a pregnancy still waiting for the hospital's first message,
 * which is now how every pregnancy starts (KIN-2060) — there is nothing to demote, and demanding a
 * number the GP cannot know would stop them correcting anything else on the record.
 *
 * A number that is given is checked either way: the column carries a >= 1 check, so a zero would
 * come back as a 500 rather than a message anyone can act on.
 */
const atLeastOneFoetus = (value) => parseInt(value, 10) >= 1;

const AT_LEAST_ONE_FOETUS_ERROR = {
  message: "A pregnancy has at least one foetus.",
};

export const makeEditPregnancyFormSchema = ({ hasStoredFoetusCount }) =>
  z
    .object({
      ...sharedPregnancyFields,
      numberOfFoetuses: hasStoredFoetusCount
        ? z
            .string()
            .min(1, { message: "Please enter the number of foetuses." })
            .refine(atLeastOneFoetus, AT_LEAST_ONE_FOETUS_ERROR)
        : z
            .string()
            .optional()
            .refine((value) => !value || atLeastOneFoetus(value), AT_LEAST_ONE_FOETUS_ERROR),
      receivingFacilityCode: z.string().optional(),
    })
    .refine(isParaWithinGravida, PARA_WITHIN_GRAVIDA_ERROR);
